import { useEffect, useRef, useCallback, RefObject } from 'react';

const DEFAULT_ALLOW_CLICK_OUTSIDE_ELEMENTS = [
  '#onetrust-banner-sdk',
  '#onetrust-pc-sdk',
  '.popover-dialog',
  '[role="dialog"][aria-label*="Cookie"]',
  '[role="region"][aria-label*="Cookie"]',
];

interface FocusTrapInstance {
  id: string;
  priority: number;
  containerRef: RefObject<HTMLElement | null>;
  isActive: boolean;
  cleanup?: () => void;
}

// Singleton manager to handle focus traps across the application
export class FocusTrapManager {
  private static instance: FocusTrapManager;
  private stack: FocusTrapInstance[] = [];
  private currentId = 0;

  static getInstance(): FocusTrapManager {
    if (!FocusTrapManager.instance) {
      FocusTrapManager.instance = new FocusTrapManager();
    }
    return FocusTrapManager.instance;
  }

  generateId(): string {
    return `focus-trap-${++this.currentId}`;
  }

  register(instance: FocusTrapInstance): void {
    // Remove existing instance with same id
    this.unregister(instance.id);

    if (instance.isActive) {
      this.stack.push(instance);
      this.sortStack();
    }
  }

  unregister(id: string): void {
    const index = this.stack.findIndex((instance) => instance.id === id);

    if (index !== -1) {
      const instance = this.stack[index];
      if (instance?.cleanup) {
        instance.cleanup();
      }
      this.stack.splice(index, 1);
    }
  }

  update(id: string, isActive: boolean): void {
    const existingIndex = this.stack.findIndex(
      (instance) => instance.id === id
    );

    if (isActive) {
      // If not in stack, this means register will be called
      if (existingIndex !== -1) {
        const instance = this.stack[existingIndex];
        if (instance) {
          instance.isActive = true;
          this.sortStack();
        }
      }
    } else {
      // Remove from stack when deactivated
      if (existingIndex !== -1) {
        const instance = this.stack[existingIndex];
        if (instance?.cleanup) {
          instance.cleanup();
        }
        this.stack.splice(existingIndex, 1);
      }
    }
  }

  private sortStack(): void {
    this.stack.sort((a, b) => b.priority - a.priority);
  }

  getTopmost(): FocusTrapInstance | null {
    return this.stack.length > 0 && this.stack[0] ? this.stack[0] : null;
  }

  isTopmost(id: string): boolean {
    const topmost = this.getTopmost();
    return topmost?.id === id;
  }

  getStack(): FocusTrapInstance[] {
    return [...this.stack];
  }

  // Check if focus target is within any active focus trap
  isWithinAnyTrap(target: Element): boolean {
    return this.stack.some((instance) =>
      instance.containerRef.current?.contains(target)
    );
  }

  // Get the focus trap that contains the target element
  getTrapContaining(target: Element): FocusTrapInstance | null {
    return (
      this.stack.find((instance) =>
        instance.containerRef.current?.contains(target)
      ) || null
    );
  }
}

export interface FocusTrapOptions {
  /** Whether to restore focus when deactivated (default: true) */
  restoreFocus?: boolean;
  /** Selector for initial focus element */
  initialFocus?: string;
  /** Selector for fallback focus element */
  fallbackFocus?: string;
  /** Prevent scrolling when focusing (default: true) */
  preventScroll?: boolean;
  /** Priority level for layered components (higher = top layer, default: 0) */
  priority?: number;
  /** Unique identifier for this focus trap instance */
  id?: string;

  allowClickOutsideElements?: string[];
}

interface FocusOptions {
  preventScroll?: boolean;
}

export interface FocusTrapReturn {
  /** Focus the first focusable element */
  focusFirst: () => boolean;
  /** Focus the last focusable element */
  focusLast: () => boolean;
  /** Get all focusable elements in the container */
  getFocusableElements: () => Element[];
  /** Check if this is the active (topmost) focus trap */
  isTopmost: () => boolean;
}

export interface UseFocusTrapProps {
  /** Ref to the container element to trap focus within */
  containerRef: RefObject<HTMLElement | null>;
  /** Whether the focus trap is active */
  isActive?: boolean;
  /** Whether the focus trap content is ready (default: true) Generally
   * only used for dynamic iframe content
   * */
  isContentReady?: boolean;
  /** Configuration options for the focus trap */
  options?: FocusTrapOptions;
}

/**
 * Focus trap hook with iframe support and layered component management
 * @param containerRef - Ref to the container element to trap focus within
 * @param isActive - Whether the focus trap is active (default false)
 * @param isContentReady - Whether the focus trap content is ready (default true)
 * @param options - UseFocusTrapProps
 * @returns FocusTrapReturn
 */
export const useFocusTrap = ({
  containerRef,
  isActive = false,
  isContentReady = true,
  options = {},
}: UseFocusTrapProps): FocusTrapReturn => {
  const {
    restoreFocus = true,
    initialFocus,
    fallbackFocus,
    preventScroll = false,
    priority = 0,
    id: providedId,
    allowClickOutsideElements = DEFAULT_ALLOW_CLICK_OUTSIDE_ELEMENTS,
  } = options;

  const shouldBeActive = isActive && isContentReady;

  const manager = FocusTrapManager.getInstance();
  const instanceIdRef = useRef<string>(providedId || manager.generateId());
  const instanceId = instanceIdRef.current;

  const previousActiveElementRef = useRef<Element | null>(null);
  const sentinelStartRef = useRef<HTMLDivElement | null>(null);
  const sentinelEndRef = useRef<HTMLDivElement | null>(null);
  const cleanupFunctionRef = useRef<(() => void) | null>(null);

  // Helper to check if an element is within any iframe in our container
  const isElementInContainerIframe = useCallback(
    (element: Element): boolean => {
      const container = containerRef?.current;
      if (!container) {
        return false;
      }

      // Check if element is in an iframe document
      if (element.ownerDocument === document) {
        return false;
      }

      // Find the iframe that contains this element
      const iframes = container.querySelectorAll('iframe');
      for (const iframe of iframes) {
        try {
          if (
            (iframe as HTMLIFrameElement).contentDocument ===
            element.ownerDocument
          ) {
            return true;
          }
        } catch {
          // Cross-origin iframe, skip
        }
      }
      return false;
    },
    [containerRef]
  );

  // Get all focusable elements within the container
  const getFocusableElements = useCallback(
    (container: HTMLElement | null): Element[] => {
      if (!container) {
        return [];
      }

      const focusableSelectors = [
        'a[href]',
        'area[href]',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex^="-"])',
      ].join(', ');

      // Get elements from main container
      const elements = Array.from(
        container.querySelectorAll(focusableSelectors)
      );

      // Find all iframes in the container
      const iframes = container.querySelectorAll('iframe');

      for (const iframe of iframes) {
        try {
          const iframeDoc = (iframe as HTMLIFrameElement).contentDocument;
          if (iframeDoc) {
            const iframeElements = Array.from(
              iframeDoc.querySelectorAll(focusableSelectors)
            );
            // Insert iframe elements after the iframe element itself
            const iframeIndex = elements.indexOf(iframe);
            if (iframeIndex !== -1) {
              elements.splice(iframeIndex + 1, 0, ...iframeElements);
            }
          }
        } catch (error) {
          console.warn('Could not access iframe content:', error);
        }
      }

      // Filter out non-focusable elements
      return elements.filter((el) => {
        const htmlEl = el as HTMLElement;

        // Check if element is in an iframe document
        const isInIframe = el.ownerDocument !== document;

        if (isInIframe) {
          // For iframe elements, check visibility within iframe context
          const iframeWindow = el.ownerDocument?.defaultView;
          if (!iframeWindow) {
            return false;
          }

          const computedStyle = iframeWindow.getComputedStyle(htmlEl);
          if (
            computedStyle.display === 'none' ||
            computedStyle.visibility === 'hidden'
          ) {
            return false;
          }
        } else {
          // Visibility check for main document elements
          if (htmlEl.offsetParent === null && el.tagName !== 'IFRAME') {
            return false;
          }
        }

        const tabIndex = el.getAttribute('tabindex');
        if (tabIndex && parseInt(tabIndex) < 0) {
          return false;
        }

        if ('disabled' in htmlEl && htmlEl.disabled) {
          return false;
        }
        if (el.getAttribute('aria-hidden') === 'true') {
          return false;
        }

        return true;
      });
    },
    []
  );

  // Focus management functions
  const focusElement = useCallback(
    (element: Element | null, opts: FocusOptions = {}): boolean => {
      if (!element || !('focus' in element)) {
        return false;
      }

      try {
        (element as HTMLElement).focus({
          preventScroll: opts.preventScroll ?? preventScroll,
        });
        return document.activeElement === element;
      } catch (error) {
        console.warn('Focus trap: Unable to focus element', element, error);
        return false;
      }
    },
    [preventScroll]
  );

  const focusFirstElement = useCallback((): boolean => {
    const container = containerRef?.current;

    if (!container || !manager.isTopmost(instanceId)) {
      return false;
    }

    // Try initial focus selector first
    if (initialFocus) {
      const initialElement = container.querySelector(initialFocus);
      if (initialElement && focusElement(initialElement)) {
        return true;
      }
    }

    // Get focusable elements
    const focusableElements = getFocusableElements(container);

    if (focusableElements.length > 0) {
      return focusElement(focusableElements[0] || null);
    }

    // Try fallback focus selector
    if (fallbackFocus) {
      const fallbackElement = container.querySelector(fallbackFocus);
      if (fallbackElement && focusElement(fallbackElement)) {
        return true;
      }
    }

    // Finally, try to focus the container itself
    const containerTabIndex = Number.parseInt(
      container.getAttribute('tabindex') || '-1'
    );
    if (containerTabIndex >= 0) {
      return focusElement(container);
    }

    return false;
  }, [
    containerRef,
    initialFocus,
    fallbackFocus,
    getFocusableElements,
    focusElement,
    instanceId,
    manager,
  ]);

  const focusLastElement = useCallback((): boolean => {
    const container = containerRef?.current;

    if (!container || !manager.isTopmost(instanceId)) {
      return false;
    }

    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      return focusElement(
        focusableElements[focusableElements.length - 1] || null
      );
    }

    return false;
  }, [containerRef, getFocusableElements, focusElement, instanceId, manager]);

  const isTopmost = useCallback((): boolean => {
    return manager.isTopmost(instanceId);
  }, [instanceId, manager]);

  // Enhanced handleFocusIn
  const handleFocusIn = useCallback(
    (event: FocusEvent): void => {
      const target = event.target as Element;

      // Only handle if this is the topmost focus trap
      if (!isTopmost()) {
        return;
      }

      const container = containerRef?.current;
      if (!container) {
        return;
      }

      // Check if focus is within our container (including iframe content)
      const isWithinContainer =
        container.contains(target) || isElementInContainerIframe(target);

      if (isWithinContainer) {
        return;
      }

      // Check if focus is within any focus trap in the stack
      if (manager.isWithinAnyTrap(target)) {
        return;
      }

      // Allow focus on permitted external elements (OneTrust, cookie dialogs, etc.)
      const allowedSelectors = allowClickOutsideElements || [];
      for (const selector of allowedSelectors) {
        if (target.closest?.(selector)) {
          return;
        }
      }

      // Focus escaped - bring it back
      event.preventDefault();
      event.stopPropagation();

      if (!focusFirstElement()) {
        // If focusFirstElement fails, try focusing the container
        focusElement(container);
      }
    },
    [
      containerRef,
      focusFirstElement,
      focusElement,
      isTopmost,
      manager,
      isElementInContainerIframe,
      allowClickOutsideElements,
    ]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      const target = event.target as Element;
      const { key, shiftKey } = event;

      // Let other key events bubble
      if (key !== 'Tab') {
        return;
      }

      // Only handle if this is the topmost focus trap
      if (!isTopmost()) {
        return;
      }

      const container = containerRef?.current;
      if (!container) {
        return;
      }

      // Check if event is within our container (including iframe content)
      const isWithinContainer =
        container.contains(target) || isElementInContainerIframe(target);
      if (!isWithinContainer) {
        return;
      }

      // Handle Tab key for focus cycling
      const focusableElements = getFocusableElements(container);

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(target);

      if (shiftKey) {
        // Shift+Tab (backward)
        if (target === firstElement || currentIndex === 0) {
          event.preventDefault();
          focusElement(lastElement || null);
        }
      } else {
        // Tab (forward)
        if (
          target === lastElement ||
          currentIndex === focusableElements.length - 1
        ) {
          event.preventDefault();
          focusElement(firstElement || null);
        }
      }
    },
    [
      containerRef,
      getFocusableElements,
      focusElement,
      isTopmost,
      isElementInContainerIframe,
    ]
  );

  // Handle clicks outside to return focus
  const handleClick = useCallback(
    (event: MouseEvent): void => {
      // Only handle if this is the topmost focus trap
      if (!isTopmost()) {
        return;
      }

      const container = containerRef?.current;
      if (!container) {
        return;
      }

      const target = event.target as Node;

      // If click is within our container, allow it
      if (container.contains(target)) {
        return;
      }

      // If click is within any focus trap, let that trap handle it
      if (manager.isWithinAnyTrap(target as Element)) {
        return;
      }
      // Check if click is on an allowed external element
      const element = target as Element;
      const allowedSelectors = allowClickOutsideElements || [];

      for (const selector of allowedSelectors) {
        if (element.closest(selector)) {
          return; // Allow the click
        }
      }

      // Click is outside all traps - return focus to topmost trap
      event.preventDefault();
      event.stopPropagation();
      focusFirstElement();
    },
    [
      containerRef,
      focusFirstElement,
      isTopmost,
      manager,
      allowClickOutsideElements,
    ]
  );

  // Sentinel elements to detect focus escape
  const createSentinels = useCallback((): void => {
    const container = containerRef?.current;
    if (!container) {
      return;
    }

    // Create start sentinel
    if (!sentinelStartRef.current) {
      const startSentinel = document.createElement('div');
      startSentinel.setAttribute('tabindex', '0');
      startSentinel.setAttribute('aria-hidden', 'true');
      startSentinel.setAttribute('data-focus-trap-sentinel', 'start');
      startSentinel.style.position = 'fixed';
      startSentinel.style.top = '0';
      startSentinel.style.left = '0';
      startSentinel.style.width = '1px';
      startSentinel.style.height = '1px';
      startSentinel.style.opacity = '0';
      startSentinel.style.pointerEvents = 'none';

      const handleStartSentinelFocus = () => {
        if (isTopmost()) {
          focusLastElement();
        }
      };

      startSentinel.addEventListener('focus', handleStartSentinelFocus);

      sentinelStartRef.current = startSentinel;
      container.insertBefore(startSentinel, container.firstChild);
    }

    // Create end sentinel
    if (!sentinelEndRef.current) {
      const endSentinel = document.createElement('div');
      endSentinel.setAttribute('tabindex', '0');
      endSentinel.setAttribute('aria-hidden', 'true');
      endSentinel.setAttribute('data-focus-trap-sentinel', 'end');
      endSentinel.style.position = 'fixed';
      endSentinel.style.top = '0';
      endSentinel.style.left = '0';
      endSentinel.style.width = '1px';
      endSentinel.style.height = '1px';
      endSentinel.style.opacity = '0';
      endSentinel.style.pointerEvents = 'none';

      const handleEndSentinelFocus = () => {
        if (isTopmost()) {
          focusFirstElement();
        }
      };

      endSentinel.addEventListener('focus', handleEndSentinelFocus);

      sentinelEndRef.current = endSentinel;
      container.appendChild(endSentinel);
    }
  }, [containerRef, focusFirstElement, focusLastElement, isTopmost]);

  // Helper to add event listeners to iframe documents
  const addIframeEventListeners = useCallback(
    (container: HTMLElement) => {
      const iframes = container.querySelectorAll('iframe');

      iframes.forEach((iframe) => {
        try {
          const iframeDoc = (iframe as HTMLIFrameElement).contentDocument;
          if (iframeDoc) {
            // Add event listeners to the iframe document
            iframeDoc.addEventListener('focusin', handleFocusIn, true);
            iframeDoc.addEventListener('keydown', handleKeyDown, true);
            iframeDoc.addEventListener('click', handleClick, true);
          }
        } catch (error) {
          console.warn('Could not add event listeners to iframe:', error);
        }
      });
    },
    [handleFocusIn, handleKeyDown, handleClick]
  );

  // Helper to remove iframe event listeners
  const removeIframeEventListeners = useCallback(
    (container: HTMLElement) => {
      const iframes = container.querySelectorAll('iframe');

      iframes.forEach((iframe) => {
        try {
          const iframeDoc = (iframe as HTMLIFrameElement).contentDocument;
          if (iframeDoc) {
            iframeDoc.removeEventListener('focusin', handleFocusIn, true);
            iframeDoc.removeEventListener('keydown', handleKeyDown, true);
            iframeDoc.removeEventListener('click', handleClick, true);
          }
        } catch {
          // Silently ignore cleanup errors
        }
      });
    },
    [handleFocusIn, handleKeyDown, handleClick]
  );

  // Helper to remove sentinels
  const removeSentinels = useCallback((): void => {
    if (sentinelStartRef.current) {
      sentinelStartRef.current.remove();
      sentinelStartRef.current = null;
    }
    if (sentinelEndRef.current) {
      sentinelEndRef.current.remove();
      sentinelEndRef.current = null;
    }
  }, []);

  // Main effect for managing focus trap
  useEffect(() => {
    if (!shouldBeActive) {
      // Cleanup logic
      manager.update(instanceId, false);
      removeSentinels();

      if (restoreFocus && previousActiveElementRef.current) {
        try {
          (previousActiveElementRef.current as HTMLElement).focus({
            preventScroll,
          });
        } catch {
          // Ignore focus errors on cleanup
        }
        previousActiveElementRef.current = null;
      }
      return;
    }

    // Store previous active element
    previousActiveElementRef.current = document.activeElement;

    createSentinels();

    // Cleanup event handlers and remove sentinels
    const cleanup = () => {
      const container = containerRef?.current;
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('click', handleClick, true);

      if (container) {
        removeIframeEventListeners(container);
      }
      removeSentinels();
    };

    cleanupFunctionRef.current = cleanup;

    manager.register({
      id: instanceId,
      priority,
      containerRef,
      isActive: true,
      cleanup,
    });

    if (manager.isTopmost(instanceId)) {
      // Focus first element with iframe support
      const focusTimeout = setTimeout(() => {
        if (manager.isTopmost(instanceId)) {
          const container = containerRef?.current;
          if (!container) {
            return;
          }

          // Add iframe event listeners
          addIframeEventListeners(container);

          document.addEventListener('focusin', handleFocusIn, true);
          document.addEventListener('keydown', handleKeyDown, true);
          document.addEventListener('click', handleClick, true);

          // Now focus the first element
          const focused = focusFirstElement();
          if (!focused && containerRef?.current) {
            containerRef.current.setAttribute('tabindex', '-1');
            focusElement(containerRef.current);
          }
        }
      }, 300);

      cleanupFunctionRef.current = () => {
        clearTimeout(focusTimeout);
        cleanup();
      };
    }

    manager.update(instanceId, true);

    return () => {
      if (cleanupFunctionRef.current) {
        cleanupFunctionRef.current();
      }
      manager.unregister(instanceId);
    };
  }, [
    shouldBeActive,
    preventScroll,
    priority,
    instanceId,
    containerRef,
    manager,
  ]);

  return {
    isTopmost,
    focusFirst: focusFirstElement,
    focusLast: focusLastElement,
    getFocusableElements: () => getFocusableElements(containerRef?.current),
  };
};
